import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LeadsLayout } from '@/components/LeadsLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AutoFollowUpSettings({ user, onLogout }) {
  const [settings, setSettings] = React.useState({
    enabled: false,
    time_based_rules: [],
    stage_based_rules: []
  });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/leads/settings/auto-followup`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    }
    setLoading(false);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/leads/settings/auto-followup`, settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  const addTimeRule = () => {
    setSettings(prev => ({
      ...prev,
      time_based_rules: [
        ...prev.time_based_rules,
        { hours_after_contact: 24, action: 'reminder', description: 'New rule' }
      ]
    }));
  };

  const addStageRule = () => {
    setSettings(prev => ({
      ...prev,
      stage_based_rules: [
        ...prev.stage_based_rules,
        { trigger_stage: 'interested', action: 'send_followup', description: 'New rule' }
      ]
    }));
  };

  const updateTimeRule = (index, field, value) => {
    const updated = [...settings.time_based_rules];
    updated[index] = { ...updated[index], [field]: value };
    setSettings(prev => ({ ...prev, time_based_rules: updated }));
  };

  const updateStageRule = (index, field, value) => {
    const updated = [...settings.stage_based_rules];
    updated[index] = { ...updated[index], [field]: value };
    setSettings(prev => ({ ...prev, stage_based_rules: updated }));
  };

  const removeTimeRule = (index) => {
    setSettings(prev => ({
      ...prev,
      time_based_rules: prev.time_based_rules.filter((_, i) => i !== index)
    }));
  };

  const removeStageRule = (index) => {
    setSettings(prev => ({
      ...prev,
      stage_based_rules: prev.stage_based_rules.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <LeadsLayout user={user} onLogout={onLogout} title="Auto Follow-up Settings">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </LeadsLayout>
    );
  }

  return (
    <LeadsLayout user={user} onLogout={onLogout} title="Auto Follow-up Settings">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Auto Follow-up Settings</h2>
            <p className="text-sm text-gray-500">Configure automated follow-up rules for leads</p>
          </div>
          <button
            onClick={saveSettings}
            disabled={saving}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

        {/* Enable/Disable Toggle */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Enable Auto Follow-ups</h3>
              <p className="text-sm text-gray-500">When enabled, follow-up reminders will be triggered automatically</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={(e) => setSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
            </label>
          </div>
        </div>

        {/* Time-Based Rules */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">⏰ Time-Based Rules</h3>
              <p className="text-sm text-gray-500">Trigger follow-ups after a specific time period</p>
            </div>
            <button
              onClick={addTimeRule}
              className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm"
            >
              + Add Rule
            </button>
          </div>

          {settings.time_based_rules.length === 0 ? (
            <p className="text-gray-400 text-center py-4">No time-based rules configured</p>
          ) : (
            <div className="space-y-3">
              {settings.time_based_rules.map((rule, index) => (
                <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">Hours After Contact</label>
                    <input
                      type="number"
                      value={rule.hours_after_contact}
                      onChange={(e) => updateTimeRule(index, 'hours_after_contact', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">Action</label>
                    <select
                      value={rule.action}
                      onChange={(e) => updateTimeRule(index, 'action', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="reminder">Show Reminder</option>
                      <option value="send_whatsapp">Send WhatsApp</option>
                      <option value="send_email">Send Email</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">Description</label>
                    <input
                      type="text"
                      value={rule.description}
                      onChange={(e) => updateTimeRule(index, 'description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <button
                    onClick={() => removeTimeRule(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Stage-Based Rules */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">📋 Stage-Based Rules</h3>
              <p className="text-sm text-gray-500">Trigger follow-ups when lead enters a specific stage</p>
            </div>
            <button
              onClick={addStageRule}
              className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm"
            >
              + Add Rule
            </button>
          </div>

          {settings.stage_based_rules.length === 0 ? (
            <p className="text-gray-400 text-center py-4">No stage-based rules configured</p>
          ) : (
            <div className="space-y-3">
              {settings.stage_based_rules.map((rule, index) => (
                <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">Trigger Stage</label>
                    <select
                      value={rule.trigger_stage}
                      onChange={(e) => updateStageRule(index, 'trigger_stage', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="contacted">Contacted</option>
                      <option value="interested">Interested</option>
                      <option value="test_drive">Test Drive</option>
                      <option value="negotiation">Negotiation</option>
                      <option value="finance_pending">Finance Pending</option>
                      <option value="dp_pending">DP Pending</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">Action</label>
                    <select
                      value={rule.action}
                      onChange={(e) => updateStageRule(index, 'action', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="send_followup">Send Follow-up</option>
                      <option value="send_update">Send Update</option>
                      <option value="notify_manager">Notify Manager</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">Description</label>
                    <input
                      type="text"
                      value={rule.description}
                      onChange={(e) => updateStageRule(index, 'description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <button
                    onClick={() => removeStageRule(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* WhatsApp Templates Info */}
        <div className="bg-green-50 rounded-xl p-6 border border-green-200">
          <h3 className="text-lg font-semibold text-green-800 mb-2">💬 WhatsApp Integration</h3>
          <p className="text-sm text-green-700 mb-3">
            WhatsApp messages are generated using templates and opened in WhatsApp Web.
            For full automation with WhatsApp Business API, please contact support for setup.
          </p>
          <div className="text-sm text-green-600">
            <p className="font-medium">Available Templates:</p>
            <ul className="list-disc list-inside mt-1">
              <li>Follow-up reminder</li>
              <li>Test drive invitation</li>
              <li>Finance update</li>
              <li>Booking confirmation</li>
              <li>Document reminder</li>
            </ul>
          </div>
        </div>

        {/* Email Integration Info */}
        <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
          <h3 className="text-lg font-semibold text-purple-800 mb-2">✉️ Email Integration</h3>
          <p className="text-sm text-purple-700 mb-3">
            Email sending is powered by Gmail SMTP. To enable automated emails:
          </p>
          <ol className="text-sm text-purple-600 list-decimal list-inside space-y-1">
            <li>Set GMAIL_USER environment variable with your Gmail address</li>
            <li>Set GMAIL_APP_PASSWORD with a Gmail App Password (not your regular password)</li>
            <li>Generate App Password from Google Account → Security → App Passwords</li>
          </ol>
        </div>
      </div>
    </LeadsLayout>
  );
}

export default AutoFollowUpSettings;
